import type { Database, Json } from '@/types/supabase'; // Combine type imports
import { withRetry } from '@/utils/error/retry';
import { handleError, logError, ErrorType, AppError } from '../utils/error/errorHandler';
import {
  generatePoemWithAIV2,
  generatePoemTitle,
} from './aiService';
import {
  getActivePoemPromptTemplateV2,
  createPromptTemplate,
  type PoemPromptTemplate,
  type ActivePoemPromptTemplate
} from './promptTemplateService';
import { createPoet, getPoetById } from './poetService';
// Assuming getWithCache *should* be exported from cacheManager, despite the previous linter error - This assumption was wrong
// Import cacheManager only from here
import { cacheManager } from '@/utils/cache/cacheManager';
import {
  // Use import type for these
  type PaginationParams,
  type PaginatedResult,
  createPaginationParams,
  createPaginatedResult,
  calculateRange
} from '@/utils/pagination/paginationUtils';
import {
  CacheEntityType,
  CACHE_TTL,
  CACHE_KEY_PATTERNS
} from '@/utils/cache/cacheConfig';
import {
  // Use direct imports, not via cacheInvalidation.*
  invalidateEntityCache,
  invalidateEntityById,
  invalidateRelatedCaches
} from '@/utils/cache/cacheInvalidation';
import { validateUUID, validateString } from '@/utils/validationWrapper';
// Correct path for schema (assuming it's in the root of schemas)
import { poemSchema } from '@/validation/schemas';
import { generateAndSaveHighlightsUnified } from './highlightService';
import { getHighlightLogger, HighlightLogContext } from '@/utils/logging/browserLogger'; // Correct path
import { ApiProvider } from '@/types/sharedTypes';
// ServiceResult is likely a type, getWithCache comes from serviceUtils
import { getWithCache, executeServiceOperation } from '@/utils/service/serviceUtils'; // Correct import for getWithCache and executeServiceOperation
import { z } from 'zod'; // Import z for validation
import { ZodError } from 'zod'; // Import ZodError for specific handling
import type { PoemHighlight } from '@/types/highlights';
import { fetchWithAuth } from '@/lib/apiUtils'; // <-- Import authenticated fetch
import type { ServiceResult } from '@/utils/service/serviceUtils';
import { AIGenerationResult } from './aiService'; // Assuming aiService exports this interface
// Import shared types
import type { PoemQueryOptions, PaginatedPoemQueryOptions } from '@/types/sharedTypes';
import { getHighlightsByPoemId } from '@/services/highlightService';
import { fetchAllPages } from '@/utils/pagination/fetchAllPages';

// Add is_protected to the base Poem type used in this service
// Use the poem_details view as the base for Poem within this service context
type Poem = Database['public']['Views']['poem_details']['Row'] & {
    is_protected?: boolean;
};

// Type for the actual poems table row (non-nullable ID)
type PoemTableRow = Database['public']['Tables']['poems']['Row'] & {
  poet_id: string; // Assume poet_id is non-nullable in the actual table row
};

// Type for poem_details view row (nullable ID, includes poet_name etc.)
type PoemDetailsViewRow = Database['public']['Views']['poem_details']['Row'] & { 
    is_protected?: boolean;
};

// Exported type combining view details with non-nullable ID and highlights
export type PoemWithHighlights = PoemDetailsViewRow & { 
  id: string; // Ensure ID is non-nullable here
  highlights: PoemHighlight[]; 
  system_prompt?: string | null; // Added system_prompt field
  ai_response: Json | null; // Add ai_response from base table
  archived_at: string | null; // Add archived_at from base table
  updated_at: string | null; // Add updated_at from base table
};

// Add is_protected to the UpdatePoem type
// Base UpdatePoem on the actual poems table
type UpdatePoem = Database['public']['Tables']['poems']['Update'] & {
    is_protected?: boolean;
};
// Base InsertPoem on the actual poems table
type InsertPoem = Database['public']['Tables']['poems']['Insert'];

// Ensure PoemDetails also includes is_protected if needed
type PoemDetails = Database['public']['Views']['poem_details']['Row'] & { 
    is_protected?: boolean;
};

/**
 * Invalidates all poem-related cache entries
 * @deprecated Use invalidateEntityCache(CacheEntityType.POEM) instead
 */
export function invalidatePoemCache(): void {
  invalidateEntityCache(CacheEntityType.POEM); // Reverted to direct call
}

/**
 * Invalidates cache entries for a specific poem
 * @param poemId - The poem's UUID
 * @deprecated Use invalidateEntityById(CacheEntityType.POEM, poemId) instead
 */
export function invalidatePoemCacheById(poemId: string): void {
  invalidateEntityById(CacheEntityType.POEM, poemId); // Reverted to direct call
}

/**
 * Retrieves all poems from the database, ordered by creation date
 * @param options - Query options including archive filtering
 * @returns Array of poem detail objects
 * @throws AppError if the operation fails
 */
export async function getAllPoems(options: PoemQueryOptions = {}): Promise<PoemDetails[]> {
  const { includeArchived = false } = options;
  console.log(`poemService: getAllPoems called (includeArchived: ${includeArchived})`);

  // Try to get from cache first using the new cache key pattern with archive state
  const cacheKey = `${CACHE_KEY_PATTERNS.ALL(CacheEntityType.POEM)}:archived=${includeArchived}`;
  const cachedPoems = cacheManager.get<PoemDetails[]>(cacheKey);
  if (cachedPoems) {
    console.log("poemService: getAllPoems returning cached data", cachedPoems.length);
    return cachedPoems;
  }

  try {
    console.log("poemService: getAllPoems fetching from API endpoint");
    const apiUrl = `/api/poems?includeArchived=${includeArchived}`;
    
    // Use fetchWithAuth
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      // Attempt to read the error response body for more details
      let errorBody: any = null;
      try {
        errorBody = await response.json();
      } catch (parseError) {
        // Ignore if response body is not valid JSON
      }
      
      console.error("poemService: getAllPoems API error", { 
        status: response.status, 
        statusText: response.statusText,
        errorBody: errorBody 
      });
      
      // Create an AppError based on the API response
      throw new AppError({
        message: `API Error: ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK, // Use NETWORK for API/fetch related errors
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody } // Include metadata
      });
    }

    const poems: PoemDetails[] = await response.json();
    console.log("poemService: getAllPoems fetched from API", poems.length);

    // Cache the result using the new cache key pattern with archive state
    cacheManager.set(cacheKey, poems, CACHE_TTL.MEDIUM);

    return poems;
  } catch (error) {
    console.error("poemService: getAllPoems error during API fetch or processing", error);
    
    // Handle potential fetch network errors or errors thrown from the !response.ok block
    const appError = handleError(error, 'getAllPoems', { includeArchived, apiUrl: `/api/poems?includeArchived=${includeArchived}` }); 
    logError(appError, 'getAllPoems');

    // If the error was classified as NOT_FOUND (e.g., 404 from API), return empty array
    // Adjust this logic if the API uses different status codes for 'not found' scenarios
    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) { 
      console.log("poemService: getAllPoems returning empty array due to API 404 or classified NOT_FOUND");
      return [];
    }
    
    // Re-throw other errors
    throw appError;
  }
}

/**
 * Retrieves paginated poems from the database, ordered by creation date
 * @param params - Pagination and filtering parameters
 * @param params.includeArchived - When true, includes archived poems in results (default: false)
 * @returns Paginated result of poem detail objects
 * @throws AppError if the operation fails
 */
export async function getPaginatedPoems(
  params: Partial<PaginatedPoemQueryOptions> = {}
): Promise<PaginatedResult<PoemDetails>> {
  const { includeArchived = false, ...paginationParams } = params;
  const pageParams = createPaginationParams(
    paginationParams.page,
    paginationParams.pageSize
  );

  // Try to get from cache first with archive state and pagination params
  const cacheKey = `${CACHE_KEY_PATTERNS.PAGINATED(CacheEntityType.POEM)}:${pageParams.page}:${pageParams.pageSize}:archived=${includeArchived}`;
  const cachedResult = cacheManager.get<PaginatedResult<PoemDetails>>(cacheKey);
  if (cachedResult) {
    console.log(`poemService: getPaginatedPoems returning cached data for page ${pageParams.page}, size ${pageParams.pageSize}, archived=${includeArchived}`);
    return cachedResult;
  }

  try {
    console.log(`poemService: getPaginatedPoems fetching from API for page ${pageParams.page}, size ${pageParams.pageSize}, archived=${includeArchived}`);
    
    // Construct the API URL with query parameters
    const query = new URLSearchParams({
      page: String(pageParams.page),
      pageSize: String(pageParams.pageSize),
      includeArchived: String(includeArchived),
    });
    const apiUrl = `/api/poems/paginated?${query.toString()}`; // Change the base API URL to the dedicated paginated endpoint

    // Use fetchWithAuth
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try {
        errorBody = await response.json();
      } catch (parseError) { /* Ignore */ }
      
      console.error("poemService: getPaginatedPoems API error", { 
        status: response.status, 
        statusText: response.statusText,
        errorBody: errorBody,
        url: apiUrl 
      });

      // Throw AppError based on API response
      throw new AppError({
        message: `API Error: ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, params }
      });
    }

    // The API is expected to return data in PaginatedResult<PoemDetails> format
    const paginatedResult: PaginatedResult<PoemDetails> = await response.json();
    console.log(`poemService: getPaginatedPoems fetched from API: ${paginatedResult.data.length} items, total ${paginatedResult.metadata.totalItems}`);

    // Cache the result
    cacheManager.set(cacheKey, paginatedResult, CACHE_TTL.SHORT); // Use appropriate TTL

    return paginatedResult;

  } catch (error) {
    console.error("poemService: getPaginatedPoems error during API fetch or processing", error);

    const appError = handleError(error, 'getPaginatedPoems', { params });
    logError(appError, 'getPaginatedPoems');

    // If the error was classified as NOT_FOUND (e.g., 404 from API), return empty paginated result
    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) {
      console.log("poemService: getPaginatedPoems returning empty result due to API 404 or classified NOT_FOUND");
      return createPaginatedResult([], 0, pageParams);
    }

    // Re-throw other errors to prevent execution from continuing
    // and allow hooks to catch the error.
    throw appError; 
  }
  // Code should not reach here if an error occurred and wasn't a 404
}

/**
 * Retrieves all poems by a specific poet
 * @param poetId - The poet's UUID
 * @param options - Query options
 * @param options.includeArchived - When true, includes archived poems in results (default: false)
 * @param options.order - Optional ordering parameters
 * @returns Array of poem objects
 * @throws AppError if the operation fails
 * 
 * @example
 * // Get only non-archived poems (default)
 * const activePoems = await getPoemsByPoet('poet-uuid');
 * 
 * // Get all poems including archived ones
 * const allPoems = await getPoemsByPoet('poet-uuid', { includeArchived: true });
 */
export async function getPoemsByPoet(
  poetId: string,
  subject: string,
  options: { includeArchived: boolean }
): Promise<PoemDetails[]> {
  // Validate ID first
  validateUUID(poetId, 'poetId');

  console.log(`poemService: getPoemsByPoet called for poet ID ${poetId}`, { options });
  const { includeArchived = false } = options;

  // Use combined cache key structure
  const cacheKey = `${CACHE_KEY_PATTERNS.BY_RELATION(CacheEntityType.POEM, CacheEntityType.POET, poetId)}:archived=${includeArchived}:subject:${subject}`;

  try {
    const poemsData = await getWithCache<PoemDetails[]>(
      cacheKey,
      async () => {
        console.log(`poemService: getPoemsByPoet fetching from API for poet ID ${poetId}`);
        // Construct API URL with query parameters
        const query = new URLSearchParams({
          poetId: poetId,
          includeArchived: String(includeArchived),
          subject: subject,
          // Add ordering if supported by API
        });
        const apiUrl = `/api/poems?${query.toString()}`; // Assuming /api/poems handles poetId filtering

        // Use fetchWithAuth
        const response = await fetchWithAuth(apiUrl);

        if (!response.ok) {
          let errorBody: any = null;
          try {
            errorBody = await response.json();
          } catch (parseError) { /* Ignore */ }

          // Handle 404 specifically - return empty array
          if (response.status === 404) {
            console.log(`poemService: getPoemsByPoet received 404 for poet ${poetId}, returning empty array.`);
            return []; // Return empty array for not found
          }
          
          console.error("poemService: getPoemsByPoet API error", { 
            status: response.status, 
            statusText: response.statusText,
            errorBody: errorBody,
            url: apiUrl,
            poetId: poetId 
          });

          // Throw AppError for other non-OK statuses
          throw new AppError({
            message: `API Error fetching poems for poet ${poetId}: ${response.statusText} (Status: ${response.status})`,
            type: ErrorType.NETWORK,
            statusCode: response.status,
            metadata: { url: apiUrl, responseBody: errorBody, poetId: poetId }
          });
        }

        const fetchedPoems: PoemDetails[] = await response.json();
        console.log(`poemService: getPoemsByPoet fetched ${fetchedPoems.length} poems from API for poet ${poetId}`);
        
        // Return fetched data to be cached by getWithCache
        return fetchedPoems; 
      },
      CACHE_TTL.SHORT // Cache TTL
    );
    return poemsData;
  } catch (error) {
    // Handle validation errors specifically
    if (error instanceof ZodError) {
       const validationError = new AppError(error, ErrorType.VALIDATION);
       logError(validationError, `getPoemsByPoet(${poetId}) - validation`);
       throw validationError;
    }
    // Handle other errors (API, unexpected) using handleError
    const appError = handleError(error, `getPoemsByPoet(${poetId})`);
    logError(appError, `getPoemsByPoet(${poetId})`);
    throw appError;
  }
}

/**
 * Retrieves paginated poems by a specific poet
 * @param poetId - The poet's UUID
 * @param params - Pagination and filtering parameters
 * @param params.includeArchived - When true, includes archived poems in results (default: false)
 * @returns Paginated result of poem objects
 * @throws AppError if the operation fails
 */
export async function getPaginatedPoemsByPoet(
  poetId: string,
  params: Partial<PaginatedPoemQueryOptions> = {}
): Promise<PaginatedResult<PoemDetails>> {
  // Validate ID first
  validateUUID(poetId, 'poetId');
  
  console.log(`poemService: getPaginatedPoemsByPoet called for poet ${poetId}`, { params });
  const { includeArchived = false, ...paginationParams } = params;
  
  const pageParams = createPaginationParams(
    paginationParams.page,
    paginationParams.pageSize
  );
  
  // Construct cache key using BY_RELATION pattern + pagination + archive state
  const cacheKey = `${CACHE_KEY_PATTERNS.BY_RELATION(CacheEntityType.POEM, CacheEntityType.POET, poetId)}:paginated:${pageParams.page}:${pageParams.pageSize}:archived=${includeArchived}`;
  const cachedResult = cacheManager.get<PaginatedResult<PoemDetails>>(cacheKey);
  if (cachedResult) {
    console.log(`poemService: getPaginatedPoemsByPoet returning cached data for poet ${poetId}, page ${pageParams.page}`);
    return cachedResult;
  }
  
  try {
    console.log(`poemService: getPaginatedPoemsByPoet fetching from API for poet ${poetId}, page ${pageParams.page}`);
    // Construct API URL with query parameters
    const query = new URLSearchParams({
      poetId: poetId,
      page: String(pageParams.page),
      pageSize: String(pageParams.pageSize),
      includeArchived: String(includeArchived),
    });
    const apiUrl = `/api/poems/paginated?${query.toString()}`; // Change the base API URL to the dedicated paginated endpoint

    // Use fetchWithAuth
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try {
        errorBody = await response.json();
      } catch (parseError) { /* Ignore */ }
      
      console.error("poemService: getPaginatedPoemsByPoet API error", { 
        status: response.status, 
        statusText: response.statusText,
        errorBody: errorBody,
        url: apiUrl,
        poetId: poetId,
        params 
      });

      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;

      throw new AppError({
        message: `API Error fetching paginated poems for poet ${poetId}: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, poetId: poetId, params }
      });
    }

    // API expected to return PaginatedResult<PoemDetails>
    const paginatedResult: PaginatedResult<PoemDetails> = await response.json();
    console.log(`poemService: getPaginatedPoemsByPoet fetched ${paginatedResult.data.length} poems from API for poet ${poetId}, page ${pageParams.page}`);

    // Cache the result
    cacheManager.set(cacheKey, paginatedResult, CACHE_TTL.SHORT);
    
    return paginatedResult;

  } catch (error) {
    const appError = handleError(error, 'getPaginatedPoemsByPoet', { poetId, params });
    logError(appError, 'getPaginatedPoemsByPoet');
    
    // For NOT_FOUND errors, return an empty paginated result
    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) {
        console.log(`poemService: getPaginatedPoemsByPoet returning empty result for poet ${poetId}, page ${pageParams.page} due to NOT_FOUND`);
        return createPaginatedResult([], 0, pageParams); // Return empty PaginatedResult
    }
    
    throw appError;
  }
}

/**
 * Retrieves a poem by ID with detailed information including highlights
 * 
 * @param id - The poem's UUID
 * @returns A Promise containing the PoemWithHighlights or null
 * @throws AppError if validation fails or if an unexpected API error occurs (non-404)
 */
export const getPoemById = async (id: string): Promise<PoemWithHighlights | null> => {
  // Validate the UUID format first - this will throw if invalid
  validateUUID(id, 'id');
  
  // Cache key for the poem
  const cacheKey = CACHE_KEY_PATTERNS.BY_ID(CacheEntityType.POEM, id);

  try {
    // Use the getWithCache helper to standardize cache handling
    const poemData = await getWithCache<PoemWithHighlights | null>(
      cacheKey,
      async () => {
        // Construct the API URL
        const apiUrl = `/api/poems/${id}`;
        console.log(`poemService: getPoemById fetching from API: ${apiUrl}`);

        // Use fetchWithAuth
        const response = await fetchWithAuth(apiUrl);

        // Special handling for 404 - return null
        if (response.status === 404) {
          console.log(`poemService: getPoemById received 404 from API for ID: ${id}`);
          return null; // Return null, DO NOT cache this null result
        }

        if (!response.ok) {
          let errorBody: any = null;
          try {
            errorBody = await response.json();
          } catch (parseError) { /* Ignore */ }
          
          console.error("poemService: getPoemById API error", { 
            status: response.status, 
            statusText: response.statusText,
            errorBody: errorBody,
            url: apiUrl 
          });

          // Throw AppError for non-404 errors
          throw new AppError({
            message: `API Error: ${response.statusText} (Status: ${response.status})`,
            type: ErrorType.NETWORK, // Assuming non-404 is network/server related
            statusCode: response.status,
            metadata: { url: apiUrl, responseBody: errorBody, poemId: id }
          });
        }

        const fetchedPoem: PoemWithHighlights = await response.json();
        console.log(`poemService: getPoemById fetched from API successfully for ID: ${id}`);

        // Validation: Ensure the fetched data looks like a PoemWithHighlights
        if (!fetchedPoem || typeof fetchedPoem !== 'object' || !fetchedPoem.id || fetchedPoem.id !== id || !Array.isArray(fetchedPoem.highlights)) {
             console.error("poemService: getPoemById received unexpected data format from API", { fetchedPoem, expectedId: id });
             throw new AppError({
                message: `API returned unexpected data format for poem ID ${id}`,
                type: ErrorType.UNEXPECTED,
                statusCode: 500, 
                metadata: { url: apiUrl, responseBody: fetchedPoem, poemId: id }
             });
        }
        
        // Cache ONLY the successfully fetched poem
        await cacheManager.set(cacheKey, fetchedPoem, CACHE_TTL.MEDIUM); 

        return fetchedPoem; 
      },
      CACHE_TTL.MEDIUM
    );
    return poemData; // Return the fetched/cached data or null
  } catch (error) {
    // Explicitly check if the error is already an AppError before handling
    if (error instanceof AppError) {
        logError(error, `getPoemById(${id}) - Propagating existing AppError`);
        throw error; // Re-throw the original AppError directly
    }
    
    // If not an AppError, use the generic handler
    const appError = handleError(error, `getPoemById(${id})`);
    logError(appError, `getPoemById(${id}) - Handled non-AppError`);
    throw appError; 
  }
};

/**
 * Create a new poem
 * @param poem - The poem data to insert
 * @returns The created poem object (from poems table row)
 */
export const createPoem = async (poem: InsertPoem): Promise<PoemTableRow> => {
  console.log(`poemService: createPoem called for title "${poem.title}"`);
  
  // Validate input
  if (!poem || typeof poem !== 'object') {
    throw new AppError('Invalid poem data provided for creation.', ErrorType.VALIDATION);
  }
  // Add more specific validation as needed (e.g., required fields)
  if (!poem.title || !poem.content || !poem.poet_id) {
      throw new AppError('Missing required fields for poem creation (title, content, poet_id).', ErrorType.VALIDATION);
  }
  validateUUID(poem.poet_id, 'poet_id');

  // Construct cache key for potential invalidation
  // Use ALL instead of BASE
  const allPoemsCacheKey = CACHE_KEY_PATTERNS.ALL(CacheEntityType.POEM);
  const byIdCacheKey = CACHE_KEY_PATTERNS.BY_ID(CacheEntityType.POEM, 'new'); // Placeholder ID before creation
  const byRelationCacheKey = CACHE_KEY_PATTERNS.BY_RELATION(CacheEntityType.POEM, CacheEntityType.POET, poem.poet_id ?? '');
  // Use ALL for subject-based invalidation or a more specific prefix if needed
  const bySubjectCacheKeyPrefix = `${CACHE_KEY_PATTERNS.ALL(CacheEntityType.POEM)}:subject:${poem.subject?.toLowerCase() ?? ''}`;
  const byPoetAndSubjectCacheKeyPrefix = `${byRelationCacheKey}:subject:${poem.subject?.toLowerCase() ?? ''}`;


  try {
    console.log("poemService: createPoem calling POST /api/poems");
    // Use fetchWithAuth
    const response = await fetchWithAuth('/api/poems', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(poem),
    });

    if (!response.ok) {
      let errorBody: any = null;
      try {
        errorBody = await response.json();
      } catch (parseError) { /* Ignore */ }

      console.error("poemService: createPoem API error", { 
        status: response.status, 
        statusText: response.statusText,
        errorBody: errorBody,
        requestBody: poem // Log what was sent
      });

      // Throw AppError for centralized handling
      throw new AppError({
        message: `API Error creating poem: ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK, // Or map status codes (e.g., 400 to VALIDATION)
        statusCode: response.status,
        metadata: { requestBody: poem, responseBody: errorBody }
      });
    }

    const createdPoem: PoemTableRow = await response.json();
    console.log(`poemService: createPoem successful, created poem ID: ${createdPoem.id}`);
    
    // Invalidate relevant caches - createdPoem.poet_id is now non-nullable string
    invalidateEntityCache(CacheEntityType.POEM);
    invalidateEntityById(CacheEntityType.POEM, createdPoem.id);
    invalidateRelatedCaches(CacheEntityType.POET, createdPoem.poet_id, createdPoem); 

    return createdPoem;

  } catch (error) {
    // Handle errors from fetch or thrown AppErrors
    const appError = handleError(error, 'createPoem', { poem });
    logError(appError, 'createPoem'); // Log the standardized error
    throw appError; // Re-throw standardized error
  }
};

/**
 * Updates an existing poem in the database via API.
 * NOTE: Checks if updates include `is_protected` and targets the admin endpoint if so.
 * Otherwise, targets the public endpoint.
 */
export const updatePoem = async (id: string, updates: UpdatePoem): Promise<PoemTableRow> => {
  console.log(`poemService: updatePoem called for ID "${id}"`);
  
  // Validate input
  if (!updates || typeof updates !== 'object') {
    throw new AppError('Invalid poem data provided for update.', ErrorType.VALIDATION);
  }
  // Add more specific validation as needed (e.g., required fields)
  if (!updates.title || !updates.content || !updates.poet_id) {
      throw new AppError('Missing required fields for poem update (title, content, poet_id).', ErrorType.VALIDATION);
  }
  validateUUID(updates.poet_id, 'poet_id');

  // Construct cache key for potential invalidation
  // Use ALL instead of BASE
  const allPoemsCacheKey = CACHE_KEY_PATTERNS.ALL(CacheEntityType.POEM);
  const byIdCacheKey = CACHE_KEY_PATTERNS.BY_ID(CacheEntityType.POEM, id);
  // Provide a default empty string if poet_id is null
  const byRelationCacheKey = CACHE_KEY_PATTERNS.BY_RELATION(CacheEntityType.POEM, CacheEntityType.POET, updates.poet_id);
  // Use ALL for subject-based invalidation or a more specific prefix if needed
  const bySubjectCacheKeyPrefix = `${CACHE_KEY_PATTERNS.ALL(CacheEntityType.POEM)}:subject:${updates.subject?.toLowerCase() ?? ''}`;
  const byPoetAndSubjectCacheKeyPrefix = `${byRelationCacheKey}:subject:${updates.subject?.toLowerCase() ?? ''}`;

  // Determine the API endpoint based on the presence of is_protected in the updates object
  const isAdminUpdate = updates.hasOwnProperty('is_protected');
  // CORRECTED: Use standard PUT /api/poems/:id endpoint for all updates, including protected status
  const apiUrl = `/api/poems/${id}`;
  // const apiUrl = isAdminUpdate ? `/api/admin/poems/${id}` : `/api/poems/${id}`;

  try {
    console.log(`poemService: updatePoem calling ${apiUrl}`); // Log the correct URL
    // Use fetchWithAuth for both public (consistency) and admin (required)
    const response = await fetchWithAuth(apiUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      let errorBody: any = null;
      try {
        errorBody = await response.json();
      } catch (parseError) { /* Ignore */ }

      console.error("poemService: updatePoem API error", { 
        status: response.status, 
        statusText: response.statusText,
        errorBody: errorBody,
        requestBody: updates // Log what was sent
      });

      // Throw AppError for centralized handling
      throw new AppError({
        message: `API Error updating poem: ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK, // Or map status codes (e.g., 400 to VALIDATION)
        statusCode: response.status,
        metadata: { requestBody: updates, responseBody: errorBody }
      });
    }

    const updatedPoem: PoemTableRow = await response.json();
    console.log(`poemService: updatePoem successful, updated poem ID: ${updatedPoem.id}`);
    
    // Invalidate relevant caches - updatedPoem.poet_id is now non-nullable string
    invalidateEntityCache(CacheEntityType.POEM);
    invalidateEntityById(CacheEntityType.POEM, id);
    invalidateRelatedCaches(CacheEntityType.POET, updatedPoem.poet_id, updatedPoem);

    return updatedPoem;

  } catch (error) {
    // Handle errors from fetch or thrown AppErrors
    const appError = handleError(error, 'updatePoem', { id, updates });
    logError(appError, 'updatePoem'); // Log the standardized error
    throw appError; // Re-throw standardized error
  }
};

/**
 * DEPRECATED - Use deletePoemById instead.
 */
export const deletePoem = async (id: string): Promise<void> => {
  console.warn('DEPRECATED: deletePoem is deprecated. Use deletePoemById instead.');
  return deletePoemById(id);
};

/**
 * Retrieves poems by subject with archive filtering
 * @param subject - The subject to filter by
 * @param options - Query options including archive filtering
 * @returns Array of poem objects
 * @throws AppError if the operation fails
 */
export async function getPoemsBySubject(
  subject: string,
  options: PoemQueryOptions = {}
): Promise<PoemDetails[]> {
  console.log(`poemService: getPoemsBySubject called for subject "${subject}"`, { options });
  const { includeArchived = false } = options;
  
  // Validate subject input (basic check)
  if (!subject || typeof subject !== 'string') {
     throw new AppError('Invalid subject provided', ErrorType.VALIDATION, null, 400, { subject });
  }

  // Use BY_SUBJECT cache key pattern
  const cacheKey = `${CACHE_KEY_PATTERNS.BY_SUBJECT(CacheEntityType.POEM, subject)}:archived=${includeArchived}`;
  const cachedPoems = cacheManager.get<PoemDetails[]>(cacheKey);
  if (cachedPoems) {
    console.log(`poemService: getPoemsBySubject returning cached data for subject "${subject}"`);
    return cachedPoems;
  }
  
  try {
    console.log(`poemService: getPoemsBySubject fetching from API for subject "${subject}"`);
    // Construct API URL with query parameters
    const query = new URLSearchParams({
      subject: subject,
      includeArchived: String(includeArchived),
      // Add ordering if supported by API
    });
    const apiUrl = `/api/poems?${query.toString()}`; // Assuming /api/poems handles subject filter

    // Use fetchWithAuth
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try {
        errorBody = await response.json();
      } catch (parseError) { /* Ignore */ }
      
      console.error("poemService: getPoemsBySubject API error", { 
        status: response.status, 
        statusText: response.statusText,
        errorBody: errorBody,
        url: apiUrl,
        subject: subject 
      });

      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;

      throw new AppError({
        message: `API Error fetching poems for subject "${subject}": ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, subject: subject, options }
      });
    }

    // API expected to return PoemDetails[] array
    const poems: PoemDetails[] = await response.json();
    console.log(`poemService: getPoemsBySubject fetched ${poems.length} poems from API for subject "${subject}"`);

    // Cache the result
    cacheManager.set(cacheKey, poems, CACHE_TTL.MEDIUM);
    
    return poems;

  } catch (error) {
    const appError = handleError(error, 'getPoemsBySubject', { subject, options });
    logError(appError, 'getPoemsBySubject');
    
    // For NOT_FOUND errors, return an empty array
    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) {
        console.log(`poemService: getPoemsBySubject returning empty array for subject "${subject}" due to NOT_FOUND`);
        return [];
    }
    
    throw appError;
  }
}

/**
 * Retrieves paginated poems by subject with archive filtering
 * @param subject - The subject to filter by
 * @param params - Pagination and filtering parameters
 * @returns Paginated result of poem objects
 * @throws AppError if the operation fails
 */
export async function getPaginatedPoemsBySubject(
  subject: string,
  params: Partial<PaginatedPoemQueryOptions> = {}
): Promise<PaginatedResult<PoemDetails>> {
  console.log(`poemService: getPaginatedPoemsBySubject called for subject "${subject}"`, { params });
  const { includeArchived = false, ...paginationParams } = params;
  
  // Validate subject input
  if (!subject || typeof subject !== 'string') {
     throw new AppError('Invalid subject provided', ErrorType.VALIDATION, null, 400, { subject });
  }
  
  const pageParams = createPaginationParams(
    paginationParams.page,
    paginationParams.pageSize
  );
  
  // Construct cache key using PAGINATED and BY_SUBJECT patterns
  const cacheKey = `${CACHE_KEY_PATTERNS.PAGINATED(CacheEntityType.POEM)}:subject:${subject}:${pageParams.page}:${pageParams.pageSize}:archived=${includeArchived}`;
  const cachedResult = cacheManager.get<PaginatedResult<PoemDetails>>(cacheKey);
  if (cachedResult) {
    console.log(`poemService: getPaginatedPoemsBySubject returning cached data for subject "${subject}", page ${pageParams.page}`);
    return cachedResult;
  }
  
  try {
    console.log(`poemService: getPaginatedPoemsBySubject fetching from API for subject "${subject}", page ${pageParams.page}`);
    // Construct API URL with query parameters
    const query = new URLSearchParams({
      subject: subject,
      page: String(pageParams.page),
      pageSize: String(pageParams.pageSize),
      includeArchived: String(includeArchived),
      // Add ordering if supported by API
    });
    const apiUrl = `/api/poems/paginated?${query.toString()}`; // Change the base API URL to the dedicated paginated endpoint

    // Use fetchWithAuth
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try {
        errorBody = await response.json();
      } catch (parseError) { /* Ignore */ }
      
      console.error("poemService: getPaginatedPoemsBySubject API error", { 
        status: response.status, 
        statusText: response.statusText,
        errorBody: errorBody,
        url: apiUrl,
        subject: subject,
        params 
      });

      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;

      throw new AppError({
        message: `API Error fetching paginated poems for subject "${subject}": ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, subject: subject, params }
      });
    }

    // API expected to return PaginatedResult<PoemDetails>
    const paginatedResult: PaginatedResult<PoemDetails> = await response.json();
    console.log(`poemService: getPaginatedPoemsBySubject fetched ${paginatedResult.data.length} poems from API for subject "${subject}", page ${pageParams.page}`);

    // Cache the result
    cacheManager.set(cacheKey, paginatedResult, CACHE_TTL.SHORT);
    
    return paginatedResult;

  } catch (error) {
    const appError = handleError(error, 'getPaginatedPoemsBySubject', { subject, params });
    logError(appError, 'getPaginatedPoemsBySubject');
    
    // For NOT_FOUND errors, return an empty paginated result
    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) {
        console.log(`poemService: getPaginatedPoemsBySubject returning empty result for subject "${subject}", page ${pageParams.page} due to NOT_FOUND`);
        return createPaginatedResult([], 0, pageParams); // Return empty PaginatedResult
    }
    
    throw appError;
  }
}

/**
 * Increments the view count for a poem
 * @param id - The poem's UUID
 * @throws AppError if the operation fails
 */
export async function incrementViewCount(id: string): Promise<void> {
  // Validate ID first
  validateUUID(id, 'id');
  
  console.log(`poemService: incrementViewCount called for poem ${id}`);
  try {
    // ID already validated above - removed redundant call

    const apiUrl = `/api/poems/${id}/increment-view`;
    console.log(`poemService: Sending POST request to ${apiUrl}`);

    // Use fetchWithAuth
    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      // No body needed for this simple increment action
    });

    if (!response.ok) {
      let errorBody: any = null;
      try {
        // Only try to parse JSON if it's not a 204 No Content
        if (response.status !== 204) { 
          errorBody = await response.json();
        }
      } catch (parseError) { /* Ignore */ }
      
      console.error("poemService: incrementViewCount API error", { 
        status: response.status, 
        statusText: response.statusText,
        errorBody: errorBody,
        url: apiUrl,
        poemId: id 
      });

      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;

      throw new AppError({
        message: `API Error incrementing view count for poem ${id}: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, poemId: id }
      });
    }

    console.log(`poemService: View count incremented successfully via API for poem ${id}`);

    // Invalidate the poem cache since the view count has changed
    invalidateEntityById(CacheEntityType.POEM, id); // Use direct function call
    console.log(`poemService: Cache invalidated for poem ${id} after view count increment`);

  } catch (error) {
    // Specific handling for Zod validation errors (from validateUUID)
    if (error instanceof ZodError) {
      const validationError = new AppError(error, ErrorType.VALIDATION);
      logError(validationError, 'incrementViewCount - validation');
      throw validationError;
    }
    // If it's already an AppError (e.g., from the API call), re-throw it
    if (error instanceof AppError) {
      logError(error, 'incrementViewCount - AppError'); 
      throw error;
    }
    // Handle other unexpected errors
    const appError = handleError(error, 'incrementViewCount - unexpected', { poemId: id });
    logError(appError, 'incrementViewCount - unexpected');
    throw appError;
  }
}

/**
 * Increments the likes count for a poem
 * @param id - The poem's UUID
 * @throws AppError if the operation fails
 */
export async function incrementLikes(id: string): Promise<void> {
  // Validate ID first
  validateUUID(id, 'id');
  
  console.log(`poemService: incrementLikes called for poem ${id}`);
  try {
    // ID already validated above - removed redundant call

    const apiUrl = `/api/poems/${id}/increment-likes`;
    console.log(`poemService: Sending POST request to ${apiUrl}`);

    // Use fetchWithAuth
    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      // No body needed for this simple increment action
    });

    if (!response.ok) {
      let errorBody: any = null;
      try {
        // Only try to parse JSON if it's not a 204 No Content
        if (response.status !== 204) { 
          errorBody = await response.json();
        }
      } catch (parseError) { /* Ignore */ }
      
      console.error("poemService: incrementLikes API error", { 
        status: response.status, 
        statusText: response.statusText,
        errorBody: errorBody,
        url: apiUrl,
        poemId: id 
      });

      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;

      throw new AppError({
        message: `API Error incrementing likes for poem ${id}: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, poemId: id }
      });
    }

    console.log(`poemService: Likes incremented successfully via API for poem ${id}`);

    // Invalidate the poem cache since the likes count has changed
    invalidateEntityById(CacheEntityType.POEM, id); // Use direct function call
    console.log(`poemService: Cache invalidated for poem ${id} after likes increment`);

  } catch (error) {
    // Specific handling for Zod validation errors (from validateUUID)
    if (error instanceof ZodError) {
      const validationError = new AppError(error, ErrorType.VALIDATION);
      logError(validationError, 'incrementLikes - validation');
      throw validationError;
    }
    // If it's already an AppError (e.g., from the API call), re-throw it
    if (error instanceof AppError) {
      logError(error, 'incrementLikes - AppError'); 
      throw error;
    }
    // Handle other unexpected errors
    const appError = handleError(error, 'incrementLikes - unexpected', { poemId: id });
    logError(appError, 'incrementLikes - unexpected');
    throw appError;
  }
}

/**
 * Gets style notes for a poet to use in the prompt
 * @param poetName - The name of the poet
 * @returns Array of style notes
 */
async function getPoetStyleNotes(poetName: string): Promise<string[]> {
  console.log(`poemService: getPoetStyleNotes called for poet "${poetName}"`);
  const defaultStyleNotes = [
    'Known for distinctive voice and imagery that reflects their worldview',
    'Uses characteristic rhythmic patterns and structural techniques',
    'Explores recurrent themes and motifs typical of their work',
    'Employs vocabulary and syntax choices consistent with their style'
  ];

  try {
    // Fetch poet details from the API by name
    const apiUrl = `/api/poets?name=${encodeURIComponent(poetName)}`;
    console.log(`poemService: Fetching poet details from ${apiUrl}`);
    const response = await fetch(apiUrl);

    if (!response.ok) {
      // If poet not found (404) or other API error, log and use defaults
      console.warn(`poemService: API error fetching poet "${poetName}" (Status: ${response.status}). Using default style notes.`);
      return defaultStyleNotes;
    }

    // API should return the poet object (or potentially an array with one poet)
    // Let's assume it returns a single object or null/empty if not found implicitly by 404 handling above.
    // If API returns an array, we might need to adjust to take the first element.
    const poetData = await response.json(); 

    // Check if we got valid data and style_points
    if (poetData && poetData.style_points) {
      const stylePoints = poetData.style_points;
      console.log(`poemService: Found style_points for "${poetName}"`, stylePoints);

      // If style_points is an array, use it directly (filter/validate)
      if (Array.isArray(stylePoints)) {
        const validNotes = stylePoints.filter((item): item is string => typeof item === 'string');
        if (validNotes.length > 0) {
          return validNotes;
        } else {
          console.warn(`poemService: style_points for "${poetName}" is an empty or invalid array. Using defaults.`);
        }
      }
      // If style_points is an object, convert it
      else if (typeof stylePoints === 'object' && stylePoints !== null) {
        const formattedNotes = Object.entries(stylePoints as Record<string, any>)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              return `${key.replace(/_/g, ' ')}: ${value.join(', ')}`;
            }
            return `${key.replace(/_/g, ' ')}: ${value}`;
          });
        if (formattedNotes.length > 0) {
          return formattedNotes;
        } else {
           console.warn(`poemService: style_points for "${poetName}" is an empty object. Using defaults.`);
        }
      }
      // If style_points is neither array nor valid object
      else {
        console.warn(`poemService: style_points for "${poetName}" has unexpected format. Using defaults.`);
      }
    } else {
      // Poet found via API but no style_points field
      console.log(`poemService: No style_points found for poet "${poetName}" in API response. Using default notes.`);
    }

    // Fallback to default notes if processing above didn't return
    return defaultStyleNotes;

  } catch (error) {
    // Handle fetch errors or JSON parsing errors
    console.error(`poemService: Error in getPoetStyleNotes for "${poetName}":`, error);
    // Fallback to generic style notes on any error during fetch/processing
    return defaultStyleNotes;
  }
}

/**
 * Generates and saves a poem
 */
export async function generateAndSavePoem(
  poetId: string,
  poetName: string,
  subject: string,
  userId?: string | null, // MODIFIED: Made userId optional (nullable)
  options: {
    customPromptTemplate?: string;
    customSystemPrompt?: string; 
    model?: string;
    tokenLimitPercentage?: number; 
    isProtected?: boolean;
  } = {}
): Promise<ServiceResult<PoemWithHighlights>> {
  const logger = getHighlightLogger('generate');
  // MODIFIED: Log userId conditionally
  logger.info('generateAndSavePoem started', { poetId, poetName, subject, userId: userId ?? 'anonymous', options }); 
  
  let ai_response: any = null;

  try {
    logger.info('Calling backend /api/poems/generate');

    const apiPayload = {
      poetId,
      poetName,
      subject,
      model: options.model,
      tokenLimitPercentage: options.tokenLimitPercentage,
    };

    const response = await fetchWithAuth('/api/poems/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(apiPayload),
    });

    if (!response.ok) {
      const body = await response.text();
      logger.error('Backend /api/poems/generate failed', { status: response.status, body });
      throw new AppError('Backend /api/poems/generate failed', ErrorType.NETWORK, null, response.status, { body });
    }

    const data = await response.json();
    const createdPoemRow: any = data.poem;
    const runId: string | null = data.runId ?? null;
    const backendHighlights: any[] = data.highlights ?? [];

    if (!createdPoemRow || !createdPoemRow.id) {
      throw new AppError('Invalid response from /api/poems/generate', ErrorType.UNEXPECTED);
    }

    logger.info('Received poem and runId from backend', { poemId: createdPoemRow.id, runId });

    const rawLines = (createdPoemRow.content as string).split('\n');
    const trimmedLines = rawLines.map((l: string) => l.trimEnd());

    ai_response = createdPoemRow.ai_response;

    const highlights = backendHighlights.length
      ? backendHighlights
      : await getHighlightsByPoemId(createdPoemRow.id);

    // Construct finalPoem
    const finalPoem: PoemWithHighlights = {
      // From PoemTableRow
      id: createdPoemRow.id!, 
      created_at: createdPoemRow.created_at, 
      title: createdPoemRow.title,
      content: createdPoemRow.content,
      poet_id: createdPoemRow.poet_id, 
      subject: createdPoemRow.subject,
      is_generated: createdPoemRow.is_generated,
      generated_prompt: createdPoemRow.generated_prompt,
      ai_response: createdPoemRow.ai_response,
      prompt_template_id: createdPoemRow.prompt_template_id, // Use ID from DB
      is_archived: createdPoemRow.is_archived,
      archived_at: createdPoemRow.archived_at,
      // Add other fields
      poet_name: poetName, 
      view_count: 0, 
      likes: 0, 
      is_protected: Boolean(createdPoemRow.is_protected), 
      highlights: highlights as any, 
      system_prompt: createdPoemRow.system_prompt,
      updated_at: createdPoemRow.created_at, 
      poet_bio: null, 
      poetry_foundation_url: null,
      style_description: null,
    };

    // Invalidate poem caches so other pages pick up the new poem immediately
    invalidateEntityCache(CacheEntityType.POEM);
    invalidateEntityById(CacheEntityType.POEM, createdPoemRow.id);

    return { data: finalPoem, error: null }; 

  } catch (error) {
    const appError = handleError(error, 'generateAndSavePoem');
    logError(appError, 'generateAndSavePoem');
    return { data: null, error: appError };
  }
}

/**
 * Retrieves poems by poet and subject
 */
export async function getPoemsByPoetAndSubject(
  poetId: string,
  subject: string,
  options: PoemQueryOptions = {}
): Promise<PoemDetails[]> {
  validateUUID(poetId, 'poetId');
  validateString(subject, 'subject');
  const { includeArchived = false } = options;
  const cacheKey = `${CACHE_KEY_PATTERNS.BY_RELATION(CacheEntityType.POEM, CacheEntityType.POET, poetId)}:subject:${subject}:archived=${includeArchived}`;

  try {
    const poemsData = await getWithCache<PoemDetails[] | null>(
      cacheKey,
      async () => {
        const query = new URLSearchParams({
          poetId: poetId,
          subject: subject,
          includeArchived: String(includeArchived),
        });
        const apiUrl = `/api/poems?${query.toString()}`; 
        const response = await fetchWithAuth(apiUrl);

        if (!response.ok) {
          const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;
          if (errorType === ErrorType.NOT_FOUND) {
              return null;
          }
          throw new AppError({ message: `API Error: ${response.statusText}`, type: errorType, statusCode: response.status });
        }
        const fetchedPoems: PoemDetails[] = await response.json();
        return fetchedPoems; 
      },
      CACHE_TTL.SHORT
    );
    return poemsData ?? []; 
  } catch (error) {
    const appError = handleError(error, 'getPoemsByPoetAndSubject', { poetId, subject });
    logError(appError, 'getPoemsByPoetAndSubject');
    if (appError.type === ErrorType.NOT_FOUND) {
        return [];
    }
    throw appError; 
  }
}

/**
 * Deletes a poem by ID (ADMIN ACTION)
 */
export const deletePoemById = async (id: string): Promise<void> => {
  validateUUID(id, 'id');
  // CORRECTED: Use standard DELETE /api/poems/:id endpoint
  const apiUrl = `/api/poems/${id}`; 
  // const apiUrl = `/api/admin/poems/${id}`; // Incorrect path
  console.log(`poemService: deletePoemById calling DELETE ${apiUrl}`);
  try {
    // Use fetchWithAuth for ADMIN action
    const response = await fetchWithAuth(apiUrl, { method: 'DELETE' });
    // ... rest of function ...
  } catch (error) {
    // ... error handling ...
  }
};

/**
 * Toggles a poem's archive status (ADMIN ACTION)
 */
export const togglePoemArchiveStatus = async (
  id: string,
  currentStatus: boolean,
  poetId: string,
  subject: string | null
): Promise<PoemTableRow> => {
  validateUUID(id, 'id');
  validateUUID(poetId, 'poetId');
  if (subject) {
    validateString(subject, 'subject'); // Validate subject if provided
  }

  const newStatus = !currentStatus; // Determine the new status
  // CORRECTED: Use the standard PUT endpoint, not the non-existent toggle-archive one
  const apiUrl = `/api/poems/${id}`; 
  // CORRECTED: Use the correct column name 'is_archived' for the request body
  const body = { is_archived: newStatus }; 

  console.log(`poemService: Toggling archive status for poem ${id} to ${newStatus} via PUT ${apiUrl}`);

  try {
    const response = await fetchWithAuth(apiUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    
    if (!response.ok) {
        // Handle non-OK response (throw AppError etc.)
        let errorBody: any = null;
        try { errorBody = await response.json(); } catch (e) {/* ignore */}
        throw new AppError({
            message: `API Error toggling archive status: ${response.statusText}`,
            type: ErrorType.NETWORK, // Or map status codes
            statusCode: response.status,
            metadata: { url: apiUrl, templateId: id, errorBody }
        });
    }
    
    const updatedPoem: PoemTableRow = await response.json();
    
    // --- Existing Cache Invalidation --- 
    invalidateEntityCache(CacheEntityType.POEM);
    invalidateEntityById(CacheEntityType.POEM, id);
    if (poetId) invalidateRelatedCaches(CacheEntityType.POET, poetId, updatedPoem);

    // --- NEW: Explicitly invalidate both getAllPoems variations --- START
    const allPoemsArchivedKey = `${CACHE_KEY_PATTERNS.ALL(CacheEntityType.POEM)}:archived=true`;
    const allPoemsNotArchivedKey = `${CACHE_KEY_PATTERNS.ALL(CacheEntityType.POEM)}:archived=false`;
    cacheManager.delete(allPoemsArchivedKey);
    cacheManager.delete(allPoemsNotArchivedKey);
    console.log(`Explicitly invalidated cache keys: ${allPoemsArchivedKey}, ${allPoemsNotArchivedKey}`);
    // --- NEW: Explicitly invalidate both getAllPoems variations --- END
    
    return updatedPoem; // Return the updated poem row
    
  } catch (error) {
    const appError = handleError(error, `togglePoemArchiveStatus(${id})`);
    logError(appError, `togglePoemArchiveStatus(${id})`);
    throw appError;
  }
};

/**
 * Toggles a poem's protected status (ADMIN ACTION)
 */
export const togglePoemProtectStatus = async (
  id: string,
  currentStatus: boolean
): Promise<PoemTableRow> => {
  validateUUID(id, 'id');
  // CORRECTED: Use standard PUT /api/poems/:id endpoint
  const apiUrl = `/api/poems/${id}`; 
  // const apiUrl = `/api/admin/poems/${id}/protect`; // Incorrect path
  const newStatus = !currentStatus;
  console.log(`poemService: togglePoemProtectStatus calling PUT ${apiUrl} to set protected=${newStatus}`);
  try {
    // Use fetchWithAuth for ADMIN action
    const response = await fetchWithAuth(apiUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' }, // Ensure content type header
      body: JSON.stringify({ is_protected: newStatus }), // Send the target status
    });
    
    if (!response.ok) {
        // Handle non-OK response (throw AppError etc.)
        let errorBody: any = null;
        try { errorBody = await response.json(); } catch (e) {/* ignore */}
        throw new AppError({
            message: `API Error toggling protect status: ${response.statusText}`,
            type: ErrorType.NETWORK, // Or map status codes
            statusCode: response.status,
            metadata: { url: apiUrl, templateId: id, errorBody }
        });
    }
    
    const updatedPoem: PoemTableRow = await response.json();
    // Invalidate caches after successful update
    invalidateEntityCache(CacheEntityType.POEM);
    invalidateEntityById(CacheEntityType.POEM, id);
    // If poetId is needed for invalidation, it needs to be passed or fetched
    // invalidateRelatedCaches(CacheEntityType.POET, updatedPoem.poet_id, updatedPoem);
    
    return updatedPoem; // Return the updated poem row
    
  } catch (error) {
    const appError = handleError(error, `togglePoemProtectStatus(${id})`);
    logError(appError, `togglePoemProtectStatus(${id})`);
    throw appError;
  }
};

/**
 * Retrieves ALL un-archived poems by iterating through the paginated endpoint.
 * Uses the generic fetchAllPages helper to honour the 100-row page-size cap
 * while still returning a single consolidated array.
 * Cached independently from other helpers so the Poets & Poems view can
 * benefit from a longer TTL.
 */
export async function getAllUnarchivedPoems(): Promise<PoemDetails[]> {
  // Define a unique cache key for the aggregated un-archived list
  const cacheKey = `${CACHE_KEY_PATTERNS.ALL(CacheEntityType.POEM)}:unarchived`;

  // Try cache first
  const cached = cacheManager.get<PoemDetails[]>(cacheKey);
  if (cached) {
    console.log('poemService: getAllUnarchivedPoems returning cached data', cached.length);
    return cached;
  }

  // Helper that fetches a single page from the existing paginated endpoint
  const fetchPage = (params: PaginationParams) =>
    getPaginatedPoems({ ...params, includeArchived: false });

  // Aggregate all pages
  const allPoems = await fetchAllPages<PoemDetails>(fetchPage);

  // Cache result with a medium TTL (same as getAllPoems)
  cacheManager.set(cacheKey, allPoems, CACHE_TTL.MEDIUM);

  return allPoems;
}
